import { useTranslation } from 'react-i18next'

import { sendRentalPortalApplicantEmail } from '../../../../../../api'
import { useNotification } from '../../../../../../hooks'
import { isDraftLoading, isDraftOpenAction } from '../../../../../Draft'
import {
  clearEmailDraftIfCreateNew,
  EmailDraftCreateActionSelectModal,
  useEmailCreationFormInitialValues,
  useEmailDraft,
  useEmailGetActiveAccountForCreate,
} from '../../../../../Email'
import {
  getIsMockApplicantId,
  postMockApplicantEmail,
} from '../../../../../RentalPortalApplicant'
import { useApplicantProfileContext } from '../../../RentalPortalApplicantProfile.hooks'
import { ApplicantProfileEmailsComposePopUp } from '../../ApplicantProfileEmailsComposePopUp'
import {
  getApplicantProfileEmailsComposeInitialValues,
  getApplicantProfileEmailsComposeTooltipKey,
  getApplicantProfileEmailsSendErrorKey,
  getApplicantProfileEmailsSendPayload,
  getHasApplicantProfileEmailsMailbox,
  toApplicantProfileEmailSendData,
  toApplicantProfileEmailsSendError,
} from '../../RentalPortalApplicantProfileEmails.utils'

export function useApplicantProfileEmailsCompose() {
  const { t } = useTranslation()

  const { handleAddSuccessNotification } = useNotification()

  const {
    applicantData,
    applicantId,
    handleCloseModal,
    handleEntityReload,
    setModal,
  } = useApplicantProfileContext()

  const { activeAccountForCreate, loadingAccount } =
    useEmailGetActiveAccountForCreate()

  const emailInitialValues = useEmailCreationFormInitialValues({
    account: activeAccountForCreate,
  })

  const {
    draft: emailDraft,
    draftId: emailDraftId,
    setDraft: setEmailDraft,
  } = useEmailDraft()

  const composeTooltipKey = getApplicantProfileEmailsComposeTooltipKey({
    applicantData,
    hasMailbox: getHasApplicantProfileEmailsMailbox({
      account: activeAccountForCreate,
      isLoading: loadingAccount,
    }),
  })

  const hasComposeBlocker = Boolean(composeTooltipKey)
  const isDraftPending = isDraftLoading(emailDraftId, emailDraft)

  const isComposeDisabled =
    hasComposeBlocker || isDraftPending || loadingAccount

  async function handleSuccessSend() {
    handleCloseModal()

    handleAddSuccessNotification(
      t('RentalPortal.ApplicantProfileEmailsSentNotificationKey')
    )

    return handleEntityReload()
  }

  async function sendEmail(payload) {
    try {
      return await sendRentalPortalApplicantEmail({
        data: toApplicantProfileEmailSendData(payload),
        id: applicantId,
      })
    } catch (error) {
      throw toApplicantProfileEmailsSendError({
        error,
        message: t(getApplicantProfileEmailsSendErrorKey(error)),
      })
    }
  }

  async function handleSubmitSend(payload) {
    // TODO: drop the mock branch once the mock sections open backend applicants only
    return getIsMockApplicantId(applicantId)
      ? postMockApplicantEmail(
          getApplicantProfileEmailsSendPayload({ applicantId, payload })
        )
      : sendEmail(payload)
  }

  function handleOpenCompose({ action, baseValues }) {
    function getIsComposeDraftOpen() {
      return isDraftOpenAction(action)
    }

    setModal(
      <ApplicantProfileEmailsComposePopUp
        getContainerIsDirty={getIsComposeDraftOpen}
        initialValues={getApplicantProfileEmailsComposeInitialValues({
          applicantData,
          emailInitialValues: baseValues,
        })}
        onClose={handleCloseModal}
        onSubmit={handleSubmitSend}
        onSuccess={handleSuccessSend}
      />
    )
  }

  function handleSubmitDraftAction({ action, initialValues }) {
    clearEmailDraftIfCreateNew(action, setEmailDraft)

    handleOpenCompose({ action, baseValues: initialValues })
  }

  function handleClickCompose() {
    if (emailDraft) {
      setModal(
        <EmailDraftCreateActionSelectModal
          draft={emailDraft}
          initialValues={emailInitialValues}
          onCancel={handleCloseModal}
          onSubmit={handleSubmitDraftAction}
        />
      )
    } else {
      handleOpenCompose({ baseValues: emailInitialValues })
    }
  }

  return {
    composeTooltipKey,
    handleClickCompose: isComposeDisabled ? null : handleClickCompose,
    isComposeDisabled,
  }
}
